import { useCallback, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { imageFileToDataUrl, trimCanvas } from "../lib/raster.ts";
import type { PendingStamp } from "../lib/ui-types.ts";
import { ColorPicker } from "./ColorPicker.tsx";

export const SIGNATURE_STORAGE_KEY = "xtract-pdf-editor-signature";

export const SIG_PAD_W = 560;
export const SIG_PAD_H = 200;

export function SignatureDialog({
  onUse,
  onClose,
}: {
  onUse: (stamp: PendingStamp) => void;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"draw" | "type" | "upload">("draw");
  const [color, setColor] = useState("#111111");
  const [typed, setTyped] = useState("");
  const [uploaded, setUploaded] = useState<PendingStamp | null>(null);
  const [saved] = useState<PendingStamp | null>(() => {
    try {
      const raw = localStorage.getItem(SIGNATURE_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as PendingStamp) : null;
    } catch {
      return null;
    }
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);

  const use = useCallback(
    (stamp: PendingStamp, remember: boolean) => {
      if (remember) {
        try {
          localStorage.setItem(SIGNATURE_STORAGE_KEY, JSON.stringify(stamp));
        } catch {
          /* quota/private mode — reuse just won't survive */
        }
      }
      onUse(stamp);
    },
    [onUse]
  );

  const confirm = useCallback(() => {
    if (mode === "draw") {
      const canvas = canvasRef.current;
      if (!canvas || !hasInk.current) return;
      const trimmed = trimCanvas(canvas);
      if (trimmed) use(trimmed, true);
      return;
    }
    if (mode === "type") {
      const text = typed.trim();
      if (!text) return;
      const font = `64px "Brush Script MT", "Segoe Script", "Apple Chancery", cursive`;
      const probe = document.createElement("canvas").getContext("2d")!;
      probe.font = font;
      const metrics = probe.measureText(text);
      const w = Math.ceil(metrics.width) + 32;
      const h = 96;
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d")!;
      ctx.font = font;
      ctx.fillStyle = color;
      ctx.textBaseline = "middle";
      ctx.fillText(text, 16, h / 2);
      const trimmed = trimCanvas(canvas);
      if (trimmed) use(trimmed, true);
      return;
    }
    if (uploaded) use(uploaded, false);
  }, [mode, typed, color, uploaded, use]);

  const canConfirm =
    mode === "draw" ? true : mode === "type" ? typed.trim().length > 0 : uploaded !== null;

  const padPoint = (e: ReactPointerEvent<HTMLCanvasElement>): [number, number] => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [
      ((e.clientX - rect.left) / rect.width) * SIG_PAD_W,
      ((e.clientY - rect.top) / rect.height) * SIG_PAD_H,
    ];
  };

  const tabBtn = (m: typeof mode, icon: string, label: string) => (
    <button
      type="button"
      aria-pressed={mode === m}
      onClick={() => setMode(m)}
      className={`inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm ${
        mode === m
          ? "bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]"
          : "hover:bg-[color:var(--color-bg-subtle)]"
      }`}
    >
      <i className={icon} aria-hidden /> {label}
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label="Add signature" className="absolute inset-0 z-40 flex items-center justify-center bg-black/40 p-6">
      <div className="w-full max-w-2xl space-y-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)] p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Add signature</h2>
          <button
            type="button"
            title="Close"
            aria-label="Close signature dialog"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        </div>
        <div className="flex items-center gap-1">
          {tabBtn("draw", "ri-sketching", "Draw")}
          {tabBtn("type", "ri-text", "Type")}
          {tabBtn("upload", "ri-upload-2-line", "Upload")}
          <div className="ml-auto flex items-center gap-1.5">
            {(mode === "draw" || mode === "type") && (
              <>
                {["#111111", "#1c4ed8"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c === "#111111" ? "Black ink" : "Blue ink"}
                    onClick={() => setColor(c)}
                    className={`h-5 w-5 rounded-full border ${
                      c === color
                        ? "border-[color:var(--color-fg)] ring-1 ring-[color:var(--color-fg)]"
                        : "border-[color:var(--color-border-strong)]"
                    }`}
                    style={{ background: c }}
                  />
                ))}
              </>
            )}
            {mode === "draw" && (
              <button
                type="button"
                onClick={() => {
                  const c = canvasRef.current;
                  c?.getContext("2d")?.clearRect(0, 0, c.width, c.height);
                  hasInk.current = false;
                }}
                className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs hover:bg-[color:var(--color-bg-subtle)]"
              >
                <i className="ri-eraser-line" aria-hidden /> Clear
              </button>
            )}
          </div>
        </div>

        {mode === "draw" && (
          <canvas
            ref={canvasRef}
            width={SIG_PAD_W}
            height={SIG_PAD_H}
            className="pe-sig-pad"
            onPointerDown={(e) => {
              e.preventDefault();
              e.currentTarget.setPointerCapture(e.pointerId);
              const ctx = e.currentTarget.getContext("2d")!;
              const [x, y] = padPoint(e);
              ctx.strokeStyle = color;
              ctx.lineWidth = 2.5;
              ctx.lineCap = "round";
              ctx.lineJoin = "round";
              ctx.beginPath();
              ctx.moveTo(x, y);
              drawing.current = true;
              hasInk.current = true;
            }}
            onPointerMove={(e) => {
              if (!drawing.current) return;
              const ctx = e.currentTarget.getContext("2d")!;
              const [x, y] = padPoint(e);
              ctx.lineTo(x, y);
              ctx.stroke();
            }}
            onPointerUp={() => {
              drawing.current = false;
            }}
          />
        )}

        {mode === "type" && (
          <div className="space-y-2">
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") confirm();
              }}
              placeholder="Type your name"
              className="h-9 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2.5 text-sm"
            />
            <div className="pe-sig-preview" style={{ color }}>
              {typed.trim() || "Signature preview"}
            </div>
          </div>
        )}

        {mode === "upload" && (
          <div className="space-y-2">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void imageFileToDataUrl(f).then(setUploaded);
              }}
              className="text-sm"
            />
            {uploaded && (
              <img
                src={uploaded.dataUrl}
                alt="Uploaded signature"
                className="max-h-40 rounded border border-[color:var(--color-border)] bg-white p-2"
              />
            )}
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          {saved && (
            <button
              type="button"
              title="Reuse your last signature"
              onClick={() => onUse(saved)}
              className="inline-flex h-10 items-center gap-2 rounded-md border border-[color:var(--color-border)] px-2 text-xs hover:bg-[color:var(--color-bg-subtle)]"
            >
              <img src={saved.dataUrl} alt="Saved signature" className="h-7 max-w-28 bg-white px-1" />
              Reuse last
            </button>
          )}
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-sm hover:bg-[color:var(--color-bg-subtle)]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canConfirm}
              onClick={confirm}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-sm text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
            >
              <i className="ri-quill-pen-line" aria-hidden /> Use signature
            </button>
          </div>
        </div>
        <p className="text-[11px] text-[color:var(--color-fg-muted)]">
          Click the page to place it. Drawn and typed signatures are kept on
          this device for one-click reuse.
        </p>
      </div>
    </div>
  );
}
